// @ts-ignore
import React from "react";

import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl } from "react-intl";

import { ImageProps } from "@atlassiansox/wpl-image";

import { ButtonColors } from "@/components/wpl/button/src";
import {
    DataEventProps,
    ExportableAdgColorTypes,
    LogoBrand,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src/index";
import SimpleImageTextSide from "@/patterns/wpl/simple-image-text-side/src";
import PaidLandingTemplate from "@/templates/paid-landing/src";

export const JiraSoftwarePagePaidLpJswRoadmaps = () => {
    const intl = useIntl();

    const props = {
        productNavProps: {
            productHref: useLocalizedHref("/software/jira"),
            productName: "jirasoftware" as LogoBrand,
            freeHref: useLocalizedHref("/software/jira/free"),
            ariaLabel: intl.formatMessage({
                id: "wacJiraSoftwareNavigation.logo-label",
                defaultMessage: "Jira Software logo"
            }),
            dataProductKey: "jiraSoftware" as ProductKeyTypes,
            showFreeHrefButton: false
        },
        bannerAlertProps: {
            identifier: "jira-software.banner-alert.atlassian-intelligence",
            text: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.banner-alert.text",
                defaultMessage:
                    "Get early access to Atlassian Intelligence, your new virtual teammate that deeply understands how teams collaborate to accelerate work."
            }),
            linkText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.banner-alert.link-text",
                defaultMessage: "Learn more"
            }),
            linkUrl: useLocalizedHref("/software/artificial-intelligence")
        },
        heroProps: {
            styles: {
                "backgroundColor": "#DEEBFF",
                "backgroundImage": `var(--hero-bg-1, linear-gradient(transparent, transparent)), linear-gradient(287.15deg, #FAFBFC 0%, #DEEBFF 8.06%, #B3D4FF 35.45%, #DEEBFF 77.6%, #FAFBFC 100%)`,
                "backgroundPosition": "top right, 0 0",
                "backgroundRepeat": "no-repeat",
                "backgroundSize": "auto 100% , auto",

                "@media only screen and (min-width: 1024px)": {
                    "--hero-bg-1": `url("https://wac-cdn.atlassian.com/dam/jcr:f96e5a0a-7af5-4cc6-884c-b51416e65e41/JSM_BG%20Graphics.png?cdnVersion=1276")`
                }
            },
            headerText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.heroHeader",
                defaultMessage: "Jira Software Timelines"
            }),
            secondaryText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.heroBody",
                defaultMessage:
                    "Plan smarter and communicate better with timelines in Jira Software"
            }),
            featuresLabel: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.heroFeaturesLabel",
                defaultMessage: "Included in your free plan:"
            }),
            featuresList: [
                intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswRoadmaps.heroChecklistItemOne",
                    defaultMessage: "10 user licenses"
                }),
                intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswRoadmaps.heroChecklistItemTwo",
                    defaultMessage: "Workflows and automation"
                }),
                intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswRoadmaps.heroChecklistItemThree",
                    defaultMessage: "Out-of-the-box timelines"
                }),
                intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswRoadmaps.heroChecklistItemFour",
                    defaultMessage: "Integrations with thousands of other tools"
                })
            ]
        },
        tabsProps: {
            headerText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.tabs.heading",
                defaultMessage:
                    "Manage dependencies and track progress on the big picture in real-time"
            }),
            styles: {},
            tabStyles: {
                defaultBG: "n0" as ExportableAdgColorTypes,
                defaultText: "n900" as ExportableAdgColorTypes,
                hoverBG: "n0" as ExportableAdgColorTypes,
                hoverText: "n900" as ExportableAdgColorTypes,
                activeBG: "b50" as ExportableAdgColorTypes,
                activeText: "n900" as ExportableAdgColorTypes
            },
            tabs: [
                {
                    textLabel: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabs.tab1.textLabel",
                        defaultMessage: "Goals"
                    }),
                    id: "goals",
                    component: Goals()
                },
                {
                    textLabel: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabs.tab2.textLabel",
                        defaultMessage: "Single source of truth"
                    }),
                    id: "singleSourceOfTruth",
                    component: SingleSourceOfTruth()
                },
                {
                    textLabel: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabs.tab3.textLabel",
                        defaultMessage: "Visualization"
                    }),
                    id: "visualization",
                    component: Visualization()
                },
                {
                    textLabel: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabs.tab4.textLabel",
                        defaultMessage: "Capacity"
                    }),
                    id: "capacity",
                    component: Capacity()
                },
                {
                    textLabel: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabs.tab5.textLabel",
                        defaultMessage: "Forecast"
                    }),
                    id: "forecast",
                    component: Forecast()
                }
            ]
        },
        ctaProps: {
            styles: {
                "color": "ffffff",
                "backgroundRepeat": "no-repeat",
                "backgroundSize": "cover",
                "padding": "16px 0",
                "backgroundImage":
                    "url(https://wac-cdn.atlassian.com/dam/jcr:efb3c307-d2fb-42af-8536-45461774796a/JSW_Blue%20Grid.png)",

                "@media only screen and (min-width: 768px)": {
                    padding: "64px 0",
                    backgroundImage:
                        "url(https://wac-cdn.atlassian.com/dam/jcr:8a9ac4bd-71f9-4af4-b859-f9c132e12f80/JSW_CTA_bg.png)"
                }
            },
            headerText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.ctaSection.buildBetterTogether",
                defaultMessage:
                    "Move fast, stay aligned, and build better - together"
            }),
            ctaProps: {
                buttonProps: {
                    color: "yellow" as ButtonColors,
                    url: "#start-trial",
                    text: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswRoadmaps.ctaSection.getItFree",
                        defaultMessage: "Get it free"
                    }),
                    dataEventProps: {
                        "data-event": "clicked",
                        "data-event-container": "ctaSection",
                        "data-event-component": "linkButton",
                        "data-product-key": "jiraSoftware-PaidLpJiraFeatures"
                    } as DataEventProps
                }
            }
        },
        testimonialProps: {
            headerText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.customerSection.trustedByCustomersWorldWide",
                defaultMessage: "Trusted by over 100,000 customers world-wide"
            }),
            logos: [
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:4cba45db-e328-4abd-88ea-bfe276355cb5/Square%20Logo.svg",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.socialProof.squareAlt",
                        defaultMessage: "Square logo"
                    }),
                    loading: "lazy"
                },
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:db51d228-2145-498b-ab73-064aa651770d/ebay%20logo.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.socialProof.ebayAlt",
                        defaultMessage: "eBay logo"
                    }),
                    loading: "lazy"
                },
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:7db3e103-186c-4413-950d-dea2f2a5755c/Spotify%20logo.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.socialProof.spotifyAlt",
                        defaultMessage: "Spotify logo"
                    }),
                    loading: "lazy"
                },
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:4d6ede5b-a9ea-410b-baab-6cb7166c080d/Cisco%20Logo.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.socialProof.ciscoAlt",
                        defaultMessage: "Cisco logo"
                    }),
                    loading: "lazy"
                }
            ] as ImageProps[]
        },
        alternatingTextAndImageProps: {
            backgroundImage:
                "linear-gradient(225deg, #deebff 0%, #ffffff 100%)",
            horizontalRuleColor: "#b2d4ff",
            imageWidth: "100%",
            imageTextSideRowOnePaddingBelowLargeScreen: "32px 0 16px",
            imageTextSideRowTwoPadding: "16px 0",
            imageTextSideRowThreePaddingBelowLargeScreen: "16px 0 32px",
            // Row 1
            rowOneHeader: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.alternatingTextAndImage.rowOneHeader",
                defaultMessage: "Customize how your team’s work flows"
            }),
            rowOneBody: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.alternatingTextAndImage.rowOneBody",
                defaultMessage:
                    "Set up, clean up, and automate even the most complicated project workflows."
            }),
            rowOneImageLink:
                "https://wac-cdn.atlassian.com/dam/jcr:e966255e-69d5-400f-a45c-dec5442225b1/JSW_Boards.png?cdnVersion=ece",
            rowOneImageAltText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.alternatingTextAndImage.rowOneImageAltText",
                defaultMessage: "Jira boards"
            }),
            //Row 2
            rowTwoHeader: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.alternatingTextAndImage.rowTwoHeaderText",
                defaultMessage: "Reports and insights"
            }),
            rowTwoBody: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.alternatingTextAndImage.rowTwoBodyText",
                defaultMessage:
                    "Out-of-the-box reports and dashboards in Jira make it easy to get critical insights into your work, so you can ensure your teams are always up to date and set up for success."
            }),
            rowTwoImageLink:
                "https://wac-cdn.atlassian.com/dam/jcr:0614d49b-6e74-471e-b0fc-912df4741c1d/JSW_ReportsInsights%202.png?cdnVersion=ece",

            rowTwoImageAltText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.alternatingTextAndImage.rowTwoImageAltText",
                defaultMessage: "Jira reports"
            }),
            //Row 3
            rowThreeHeader: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.alternatingTextAndImage.rowThreeHeaderText",
                defaultMessage: "Bye-bye, spreadsheets"
            }),
            rowThreeBody: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.alternatingTextAndImage.rowThreeBodyText",
                defaultMessage:
                    "Keep every detail of a project centralized in real time so up-to-date info can flow freely across people, teams, and tools."
            }),
            rowThreeImageLink:
                "https://wac-cdn.atlassian.com/dam/jcr:ee041172-56ca-478b-aa45-f5cda74bc8f2/JSW_Bye%20Bye%20Spreadseets.png?cdnVersion=ece",
            rowThreeImageAltText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswRoadmaps.spreadsheetsSideBySideImageTextSide.RowThreeImageAltText",
                defaultMessage: "Spreadsheets"
            })
        }
    };

    return <PaidLandingTemplate {...props} />;
};

/* Tabs */
const Capacity = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.capacityHeaderText",
        defaultMessage: "Plan with team capacity in mind (Advanced Only)"
    });
    const bodyText = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.capacityBodyText",
        defaultMessage:
            "Keep your goals on track by accounting for whether your teams have bandwidth to complete the work they’ve scoped. See multiple teams' capacity on a sprint-by-sprint basis."
    });
    const imageSrc =
        "https://wac-cdn.atlassian.com/dam/jcr:64584cc4-d150-412b-ad48-d1ef7e65cd6f/JSW_Capacity.png?cdnVersion=ece";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.capacityImageAlt",
        defaultMessage: "Jira Advanced Roadmaps"
    });

    return (
        <SimpleImageTextSide
            className="simple-image-text-side-tabs"
            headerText={title}
            bodyText={bodyText}
            imageLink={imageSrc}
            imageAltText={imageAlt}
            rowProps="4-8"
        />
    );
};

const Forecast = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.forecastHeaderText",
        defaultMessage: "Envision different scenarios (Advanced Only)"
    });
    const bodyText = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.forecastBodyText",
        defaultMessage:
            "Paint a more complete picture of the potential options and outcomes. With Advanced Timelines you can create multiple versions of your roadmap to account for different options or best and worst-case scenarios."
    });
    const imageSrc =
        "https://wac-cdn.atlassian.com/dam/jcr:0bd4ccf1-6de9-44dc-8688-7aeebf5c95ea/JSW_Forecast.png?cdnVersion=ece";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.forecastImageAlt",
        defaultMessage: "Jira advanced roadmaps"
    });

    return (
        <SimpleImageTextSide
            className="simple-image-text-side-tabs"
            headerText={title}
            bodyText={bodyText}
            imageLink={imageSrc}
            imageAltText={imageAlt}
            rowProps="4-8"
        />
    );
};

const Goals = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.goalsHeaderText",
        defaultMessage: "Roll your work up to bigger goals (Basic)"
    });
    const bodyText = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.goalsBodyText",
        defaultMessage:
            "Ladder your work up to the epic level to see your team’s progress in real-time. Timelines offer quick and easy planning and are available as part of all Jira Software plans."
    });
    const imageSrc =
        "https://wac-cdn.atlassian.com/dam/jcr:29c851be-07cd-42e6-b159-2db81c6ab81d/timeline_view_updated.png?cdnVersion=ece";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.goalsImageAlt",
        defaultMessage: "Goals view in Jira"
    });

    return (
        <SimpleImageTextSide
            className="simple-image-text-side-tabs"
            headerText={title}
            bodyText={bodyText}
            imageLink={imageSrc}
            imageAltText={imageAlt}
            rowProps="4-8"
        />
    );
};

const Visualization = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.visualizationHeaderText",
        defaultMessage: "Get ahead of dependencies"
    });
    const bodyText = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.visualizationBodyText",
        defaultMessage:
            "Visualize dependencies that could impact your team so you’re able to account for them when you’re making plans."
    });
    const imageSrc =
        "https://wac-cdn.atlassian.com/dam/jcr:6646630b-565d-4406-b568-5f629e366c96/JSW_Visualization.png?cdnVersion=ece";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.visualizationImageAlt",
        defaultMessage: "Jira screenshot"
    });

    return (
        <SimpleImageTextSide
            className="simple-image-text-side-tabs"
            headerText={title}
            bodyText={bodyText}
            imageLink={imageSrc}
            imageAltText={imageAlt}
            rowProps="4-8"
        />
    );
};

const SingleSourceOfTruth = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.truthHeaderText",
        defaultMessage: "Track the big picture"
    });
    const bodyText = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.truthBodyText",
        defaultMessage:
            "Always keep the big picture front and center. Track your progress, better manage dependencies, and refine your plan for a single team."
    });
    const imageSrc =
        "https://wac-cdn.atlassian.com/dam/jcr:2ab7cb93-a1c2-4fd3-9491-cd232c6c25d7/JSW_SingleSource.png?cdnVersion=ece";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswRoadmaps.tabGroup.truthImageAlt",
        defaultMessage: "Single source of truth in Jira"
    });

    return (
        <SimpleImageTextSide
            className="simple-image-text-side-tabs"
            headerText={title}
            bodyText={bodyText}
            imageLink={imageSrc}
            imageAltText={imageAlt}
            rowProps="4-8"
        />
    );
};
